import { useState, useEffect, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { fmtDT } from "../crm/dateFormat";
import app from "../firebase/config"; // abc-dashboard's own Firebase app instance (default export)

// "Bank Sync" tab of the Errors screen: failed or unfinished bank-sync runs
// of the last 7 days across all societies. Staff-only on the server
// (listBankSyncFailures).
const functions = getFunctions(app, "asia-south1");
const listBankSyncFailuresFn = httpsCallable(functions, "listBankSyncFailures");

const card = { background: "#1a1a24", border: "1px solid #2d2d3a", borderRadius: "10px", padding: "14px" };
const muted = { color: "#555570", fontSize: "11px" };

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return fmtDT(d);
}

export default function BankSyncFailuresTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const result = await listBankSyncFailuresFn();
      setData(result.data);
    } catch (e) {
      console.error("Fetch bank sync failures failed:", e);
      setErr("Couldn't load Bank Sync failures. Please contact the developer.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const failures = data?.failures || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
        <div style={{ ...muted, flex: 1 }}>
          Bank-sync runs of the last 7 days that failed or did not finish, across all societies.
          {data ? ` Checked ${data.runsChecked} runs in ${data.societiesChecked} societies.` : ""}
        </div>
        <button
          onClick={load}
          style={{ background: "#1a1a24", color: "#fff", border: "1px solid #2d2d3a", borderRadius: "8px", padding: "8px 14px", cursor: "pointer" }}
        >
          Refresh
        </button>
      </div>

      {(data?.warnings || []).map((w) => (
        <div key={w} style={{ color: "#ffb74d", fontSize: "12px" }}>⚠ {w}</div>
      ))}

      {loading && <div style={{ color: "#aab2c5", padding: "12px 0" }}>Loading…</div>}
      {!loading && err && <div style={{ color: "#ff5252", padding: "12px 0" }}>{err}</div>}
      {!loading && !err && failures.length === 0 && (
        <div style={{ color: "#aab2c5", padding: "12px 0" }}>No failed bank-sync runs in the last 7 days. 🎉</div>
      )}

      {!loading && !err && failures.map((f) => (
        <div key={f.id} style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", color: "#fff", fontSize: "13px" }}>
            <span>
              {f.societyName || f.societyId}
              {f.societyName ? <span style={muted}> · {f.societyId}</span> : null}
            </span>
            <span style={muted}>{fmt(f.requestedAt)}</span>
          </div>
          <div style={{ color: "#ff8a80", fontSize: "13px", marginTop: "8px", wordBreak: "break-word" }}>{f.error}</div>
          <div style={{ ...muted, marginTop: "8px" }}>
            {f.kind === "unfinished" ? "Did not finish" : "Failed"}
            {" · "}{f.source === "timer" ? "Automatic" : "Sync button"}
            {f.live === false ? " · Practice run" : ""}
            {f.attempts ? ` · Tries: ${f.attempts}` : ""}
          </div>
        </div>
      ))}
    </div>
  );
}